/**
 * Types for Bender's Sync Target registry and Box folder probe.
 *
 * These are hand-written rather than generated: `bender-api.ts` is a hand-written
 * RTK Query slice (Leela's codegen only covers its own backend's OpenAPI spec).
 * They mirror Bender's `SyncTargetDto` / `CreateSyncTargetDto` / `UpdateSyncTargetDto`
 * with `Date` fields as ISO `string`s, which is what crosses the wire.
 *
 * The API slice passes Bender's response through unchanged, so nested objects such as `run` are
 * untyped JSON at runtime; consumers read them through a guard (see `readRun`).
 *
 * This mirror includes Bender's exclusion fields and default locale so Leela can display and edit
 * them, while schedule and status editing remain intentionally outside this UI's scope.
 *
 * See docs/system-specs/modules/box-folders.md
 */
import { type SyncTargetLocale } from 'src/constants';

/** Bender's only sync target type today. Note the value is lowercase. */
export const SyncTargetTypes = {
  BOX: 'box',
} as const;
export type SyncTargetType = (typeof SyncTargetTypes)[keyof typeof SyncTargetTypes];

/**
 * `ACTIVE` / `PAUSED` are the only statuses an API caller can set. `ERROR`,
 * `QUEUED`, and `IN_PROGRESS` are pipeline-managed and rejected on PATCH.
 */
export const SyncTargetStatuses = {
  ACTIVE: 'ACTIVE',
  PAUSED: 'PAUSED',
  ERROR: 'ERROR',
  QUEUED: 'QUEUED',
  IN_PROGRESS: 'IN_PROGRESS',
} as const;
export type SyncTargetStatus = (typeof SyncTargetStatuses)[keyof typeof SyncTargetStatuses];

export const SyncTargetErrorTypes = {
  ACCESS_LOST: 'ACCESS_LOST',
  SCHEDULE_PROVISIONING_FAILED: 'SCHEDULE_PROVISIONING_FAILED',
  ENUMERATION_FAILED: 'ENUMERATION_FAILED',
} as const;
export type SyncTargetErrorType = (typeof SyncTargetErrorTypes)[keyof typeof SyncTargetErrorTypes];

/** Bender's progress for the current or most recent run, in nodes (folder listings) and batches (≤50 files). Absent before a target's first run. */
export interface SyncRunDto {
  startedAt?: string;
  lastEnumeratedAt?: string;
  nodesOutstanding?: number;
  nodesSucceeded?: number;
  nodesFailed?: number;
  filesQueued?: number;
  batchesQueued?: number;
  batchesQualified?: number;
  lastQualifiedAt?: string;
}

export interface SyncTargetDto {
  /** Composite registry key, `"{syncTargetType}:{targetId}"` — e.g. `box:409789436486`. */
  syncTargetId: string;
  syncTargetType: SyncTargetType;
  /** Bare numeric Box folder id, canonicalized server-side. */
  targetId: string;
  /** EventBridge Scheduler expression — `rate(...)` or `cron(...)`. */
  refreshSchedule: string;
  status: SyncTargetStatus;
  /** Box folder IDs whose subtrees are excluded from qualification. Absent when never set. */
  excludedFolderIds?: string[];
  /** Case-insensitive regex sources matched against filenames and folder paths. Absent when never set. */
  excludedPathnames?: string[];
  /** Locale given to assets whose filename and folders name none. Absent when never set. */
  defaultLocale?: SyncTargetLocale;
  /**
   * Retained across refresh and pause. Only meaningful when `status` is `ERROR`
   * — never render these based on presence alone or you show resolved errors forever.
   */
  errorType?: SyncTargetErrorType;
  errorAt?: string;
  run?: SyncRunDto;
  createdAt?: string;
  updatedAt?: string;
  userAlias: string;
}

export interface SyncTargetListResponse {
  data: SyncTargetDto[];
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

/** `page` is 1-indexed (default 1); `pageSize` is 1..100 (default 20). */
export interface SyncTargetListArgs {
  page?: number;
  pageSize?: number;
}

export interface CreateSyncTargetRequest {
  /**
   * Always send this explicitly. Bender defaults it via a class field initializer,
   * which is not something to depend on across a wire boundary.
   */
  syncTargetType: SyncTargetType;
  /** Bender accepts a bare numeric id or a Box folder URL; we always send the bare id. */
  targetId: string;
  refreshSchedule: string;
  userAlias: string;
  /** Omitted rather than sent empty, so a create without exclusions keeps its original body. */
  excludedFolderIds?: string[];
  excludedPathnames?: string[];
  /** Omitted when the user picks none; Bender rejects its `noLocale` filler and hyphenated forms. */
  defaultLocale?: SyncTargetLocale;
}

/** Args for PATCH /sync-targets/{id}. Both arrays always sent in full (Bender replaces wholesale). */
export interface UpdateSyncTargetRequest {
  syncTargetId: string;
  excludedFolderIds: string[];
  excludedPathnames: string[];
  /** A locale sets the default and `null` removes it; omit it to leave the stored default unchanged. */
  defaultLocale?: SyncTargetLocale | null;
}

/** Response of Bender's `GET /box?folderId=` folder-lookup mode. */
export interface BoxFolder {
  id: string;
  name: string;
  etag?: string;
  itemStatus?: string;
  itemCollection?: {
    totalCount: number;
  };
}
